import { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { BRAND_CONTACT } from "@/data/products";
import { MapPin, Phone, Mail, ArrowRight, ExternalLink } from "lucide-react";

const MAP_EMBED = `https://maps.google.com/maps?q=${BRAND_CONTACT.mapsQuery}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

const Contacto = () => {
  const { t } = useLang();
  const [form, setForm] = useState({
    company: "",
    name: "",
    activity: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    const body = encodeURIComponent(
      `Empresa: ${form.company}\nPersona de contacto: ${form.name}\nActividad: ${form.activity}\nE-mail: ${form.email}\nTeléfono: ${form.phone}\n\n${form.message}`
    );
    window.location.href = `mailto:${BRAND_CONTACT.email}?subject=${encodeURIComponent(
      form.subject || "Contacto web — La Criollita"
    )}&body=${body}`;
    setSent(true);
    setTimeout(() => setSent(false), 6000);
  };

  return (
    <div data-testid="page-contacto">
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-[80vh]">
        {/* Left: form */}
        <div className="px-6 md:px-12 lg:px-20 py-16 md:py-24 order-2 lg:order-1">
          <div className="max-w-xl">
            {/* Form block with subtle surface background */}
            <div className="bg-surface px-6 md:px-10 lg:px-12 py-10 md:py-14">
              <div className="text-[11px] uppercase tracking-[0.3em] text-caramel mb-6 font-medium">
                {t.contact.overline}
              </div>
              <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tighter leading-[0.95] text-ink">
                {t.contact.title}
              </h1>
              <p className="mt-6 text-base md:text-lg text-ink-muted font-light leading-relaxed">
                {t.contact.sub}
              </p>

            <form onSubmit={onSubmit} className="mt-12 space-y-8" data-testid="contact-form">
              {/* Empresa + Persona de contacto */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="border-b border-bone pb-2">
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-2">
                    {t.contact.company}
                  </label>
                  <input
                    name="company"
                    type="text"
                    value={form.company}
                    onChange={onChange}
                    data-testid="contact-input-company"
                    className="w-full bg-transparent outline-none text-ink font-light text-base py-1"
                  />
                </div>
                <div className="border-b border-bone pb-2">
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-2">
                    {t.contact.name}
                  </label>
                  <input
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={onChange}
                    data-testid="contact-input-name"
                    className="w-full bg-transparent outline-none text-ink font-light text-base py-1"
                  />
                </div>
              </div>

              {/* Actividad radio group */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-4">
                  {t.contact.activity}
                </label>
                <div className="flex flex-wrap gap-x-8 gap-y-3" role="radiogroup">
                  {t.contact.activityOptions.map((opt) => {
                    const isActive = form.activity === opt;
                    return (
                      <label
                        key={opt}
                        data-testid={`activity-${opt.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        className={`flex items-center gap-2.5 cursor-pointer group ${
                          isActive ? "text-wine" : "text-ink hover:text-wine"
                        }`}
                      >
                        <input
                          type="radio"
                          name="activity"
                          value={opt}
                          checked={isActive}
                          onChange={onChange}
                          className="sr-only"
                        />
                        <span
                          className={`relative w-4 h-4 border transition-colors ${
                            isActive ? "border-wine bg-wine" : "border-ink-muted group-hover:border-wine"
                          }`}
                        >
                          {isActive && (
                            <span className="absolute inset-1 bg-bg" />
                          )}
                        </span>
                        <span className="text-sm font-light">{opt}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Email + Teléfono */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="border-b border-bone pb-2">
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-2">
                    {t.contact.email}
                  </label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-input-email"
                    className="w-full bg-transparent outline-none text-ink font-light text-base py-1"
                  />
                </div>
                <div className="border-b border-bone pb-2">
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-2">
                    {t.contact.phone}
                  </label>
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="contact-input-phone"
                    className="w-full bg-transparent outline-none text-ink font-light text-base py-1"
                  />
                </div>
              </div>

              {/* Asunto */}
              <div className="border-b border-bone pb-2">
                <label className="block text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-2">
                  {t.contact.subject}
                </label>
                <input
                  name="subject"
                  type="text"
                  value={form.subject}
                  onChange={onChange}
                  data-testid="contact-input-subject"
                  className="w-full bg-transparent outline-none text-ink font-light text-base py-1"
                />
              </div>

              <div className="border-b border-bone pb-2">
                <label className="block text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-2">
                  {t.contact.message}
                </label>
                <textarea
                  name="message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-input-message"
                  className="w-full bg-transparent outline-none text-ink font-light text-base py-1 resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                className="inline-flex items-center gap-3 bg-wine text-bg px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-wine-hover transition-colors group"
              >
                {t.contact.send}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </button>
              {sent && (
                <p data-testid="contact-sent" className="text-caramel text-sm font-light">
                  ✓ {t.contact.sent}
                </p>
              )}
            </form>
            </div>
            {/* /End of form surface block */}
          </div>
        </div>

        {/* Right: Google Map at half height + info below — aligned with left form card */}
        <div className="relative order-1 lg:order-2 flex flex-col px-6 md:px-12 lg:px-20 py-16 md:py-24">
          {/* Map iframe at half height */}
          <div className="relative h-[300px] lg:h-[420px] min-h-[300px] bg-ink">
            <iframe
              data-testid="contact-map-iframe"
              title="Ubicación La Criollita"
              src={MAP_EMBED}
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(15%) contrast(1.03)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full"
            />
          </div>
          {/* Direct contacts directly below map (replaces "Cómo llegar" block) */}
          <div
            data-testid="contact-info-section"
            className="px-0 py-10 md:py-12 grid grid-cols-1 sm:grid-cols-3 gap-8"
          >
            <div>
              <MapPin className="w-5 h-5 text-wine mb-3" strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-[0.25em] text-caramel mb-2 font-medium">
                {t.contact.addressTitle}
              </div>
              <p className="text-sm text-ink-muted font-light leading-relaxed">
                C/ Camí de Muntanya, 7 — 1º<br />
                Polígono Industrial de Marratxí<br />
                07141 Mallorca · España
              </p>
            </div>
            <div>
              <Phone className="w-5 h-5 text-wine mb-3" strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-[0.25em] text-caramel mb-2 font-medium">
                {t.contact.phoneTitle}
              </div>
              <a
                href={`tel:${BRAND_CONTACT.phone}`}
                data-testid="contact-phone-link"
                className="text-sm text-ink-muted font-light hover:text-wine"
              >
                {BRAND_CONTACT.phone}
              </a>
            </div>
            <div>
              <Mail className="w-5 h-5 text-wine mb-3" strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-[0.25em] text-caramel mb-2 font-medium">
                {t.contact.emailTitle}
              </div>
              <a
                href={`mailto:${BRAND_CONTACT.email}`}
                data-testid="contact-email-link"
                className="text-sm text-ink-muted font-light hover:text-wine break-all"
              >
                {BRAND_CONTACT.email}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contacto;
